import React, { useState, useCallback, useRef } from 'react'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { permissionType } from 'src/lib/utils/constants'
import { useLocale } from 'src/lib/hooks/useLocal'
import { useSegmentsManagerList } from 'src/lib/hooks/useSegmentsManagerList'
import { SegmentListItemType, SegmentSortOrderType, SegmentTemplateType } from 'src/lib/types/segment'
import SegmentTable from 'src/components/segments/SegmentTable'
import ConfirmationDialog from 'src/components/common/feedback-and-dialog/ConfirmationDialog'
import DeleteConfirmationPopup from 'src/components/common/dialog/DeleteConfirmationPopup'
import GenericSelectionModal from 'src/components/common/select-component/GenericSelectionModal'
import { useLoader } from 'src/lib/contexts/LoaderContext'
import { deleteSegmentById, toggleSegmentStatusById } from 'src/lib/utils/segmentsHelper'
import { errorToast, successToast } from 'src/lib/utils/toasters'
import { EntityName, useActivityLogs } from 'src/lib/hooks/useActivityLogs'
import { UsersThree, MagnifyingGlass, X, SortAscending } from '@phosphor-icons/react'
import { Button, Icon, IconButton, Input } from '@shopflo/ui'
import { motion, AnimatePresence } from 'motion/react'
import ManualSegmentCreationDialog from 'src/components/segments/ManualSegmentCreationDialog'
import ManualSegmentCreationFlow from 'src/components/segments/ManualSegmentCreationFlow'
import CustomerTagSegmentCreationFlow from 'src/components/segments/CustomerTagSegmentCreationFlow'
import TemplatesView from 'src/components/segments/TemplatesView'
import GenericSlideInDialog from 'src/components/common/containers/GenericSlideInDialog'
import { SegmentProvider } from 'src/lib/contexts/SegmentContext'
import { DynamicSegmentProvider } from 'src/lib/contexts/DynamicSegmentContext'
import DynamicSegmentCreationFlow from 'src/components/segments/DynamicSegmentCreationFlow'
import useGTM from 'src/lib/hooks/useGTM'
import { useRouter } from 'next/router'
import { useUser } from 'src/lib/contexts/UserContext'
import useNavigation from 'src/lib/hooks/useNavigation'
import { SegmentListSortOptions } from 'src/assets/SegmentsConfig'

interface SegmentsProps { }

/**
 * Resume navigation after Leave. Primary nav uses buttons + router, not `<a href>` — targets expose
 * `data-shopflo-nav-path` (see navigation components). `<a href>` is still supported.
 */
function getPendingNavPathFromClick(e: MouseEvent): string | null {
  const el = e.target instanceof Element ? e.target : null
  const navMarked = el?.closest('[data-shopflo-nav-path]')
  const fromNav = navMarked?.getAttribute('data-shopflo-nav-path')
  if (fromNav?.trim()) return fromNav.trim()

  const anchor = el?.closest('a[href]')
  if (!anchor) return null
  const href = anchor.getAttribute('href')
  if (!href || href === '#' || href.trim() === '' || href.startsWith('javascript:')) return null
  return href
}

const Segments: React.FC<SegmentsProps> = () => {
  const { t } = useLocale()
  const {
    getters: { merchant }
  } = useMerchant()
  const router = useRouter()
  const { setLoaderProperties } = useLoader()
  const { sendActivityLog } = useActivityLogs()
  const { sendGTMTrigger } = useGTM()
  const [searchText, setSearchText] = useState<string>('')
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState<boolean>(false)
  const [isDeleteLoading, setIsDeleteLoading] = useState<boolean>(false)
  const [isToggleSegmentConfirmOpen, setIsToggleSegmentConfirmOpen] = useState<boolean>(false)
  const [selectedSegmentId, setSelectedSegmentId] = useState<string>('')
  const [selectedSegmentActiveStatus, setSelectedSegmentActiveStatus] = useState<boolean | null>(false)
  const [isSegmentDialogOpen, setIsSegmentDialogOpen] = useState<boolean>(false)
  const [editSegmentId, setEditSegmentId] = useState<string | undefined>(undefined)
  const [isTemplatesDialogOpen, setIsTemplatesDialogOpen] = useState<boolean>(false)
  const [templatesDialogView, setTemplatesDialogView] = useState<'templates' | 'static-creation' | 'dynamic-creation' | 'customer-tag-creation'>('templates')
  const [pendingDynamicTemplateId, setPendingDynamicTemplateId] = useState<string | null>(null)
  const [isDynamicPreviewVisible, setIsDynamicPreviewVisible] = useState(false)
  const pendingSegmentLeaveHrefRef = useRef<string | null>(null)
  /** After templates exit, widen dialog — avoids templates UI jumping left while panel expands (right-anchored sheet). */
  const widenDialogAfterTemplatesExitRef = useRef(false)
  const [isSearchExpanded, setIsSearchExpanded] = useState<boolean>(false)
  /** Selected sort option - will be passed to backend when API supports it */
  const [sortOrder, setSortOrder] = useState<SegmentSortOrderType>('NEWEST')

  const { canAccess } = useUser()
  const hasDynamicSegmentsPermission = canAccess(permissionType.DYNAMIC_SEGMENTS, 0)
  const {
    getters: { sidebarOpen, isTablet },
    actions: { navigateWithSubmenuHandling }
  } = useNavigation()
  const { segmentList, loading, isLoadingMore, hasMore, loadMore, callGetSegments } = useSegmentsManagerList({
    accountId: merchant?.accountId,
    hasDynamicSegmentsPermission,
    sortOrder,
    searchText,
    t
  })
  const sidebarWidth = isTablet ? 0 : (sidebarOpen ? 282 : 309)

  const showTemplatesView = !loading && segmentList.length === 0 && searchText === ''

  const handleDeleteSegment = (segmentId: string) => {
    setIsDeleteConfirmOpen(true)
    setSelectedSegmentId(segmentId)
  }

  const handleToggleSegment = (segmentId: string, isEnabled: boolean) => {
    setIsToggleSegmentConfirmOpen(true)
    setSelectedSegmentId(segmentId)
    setSelectedSegmentActiveStatus(isEnabled)
  }

  const handleCreateSegment = () => {
    sendGTMTrigger('segment_creation_started', 'click')
    setIsTemplatesDialogOpen(true)
  }

  const handleCreateStaticSegment = () => {
    setTemplatesDialogView('static-creation')
    setIsTemplatesDialogOpen(true)
  }

  const handleTemplatesDialogClose = useCallback((open: boolean) => {
    setIsTemplatesDialogOpen(open)
    if (!open) {
      setTemplatesDialogView('templates')
      setIsDynamicPreviewVisible(false)
      setPendingDynamicTemplateId(null)
      widenDialogAfterTemplatesExitRef.current = false
    }
  }, [])

  const clearPendingDynamicTemplate = useCallback(() => {
    setPendingDynamicTemplateId(null)
  }, [])

  const handleCreateCustomerTagSegment = () => {
    setEditSegmentId(undefined)
    setTemplatesDialogView('customer-tag-creation')
    setIsTemplatesDialogOpen(true)
  }

  const handleCreateDynamicSegment = () => {
    widenDialogAfterTemplatesExitRef.current = true
    setTemplatesDialogView('dynamic-creation')
    setIsTemplatesDialogOpen(true)
  }

  const handleSegmentCreationOutsideClick = useCallback(
    (e: MouseEvent) => {
      // Template picker only — close without unsaved-changes prompt (no draft yet)
      if (templatesDialogView === 'templates') {
        handleTemplatesDialogClose(false)
        return
      }
      pendingSegmentLeaveHrefRef.current = getPendingNavPathFromClick(e)
      const confirmMessage = `${t('segment_creation.leave_flow_title')}\n\n${t('segment_creation.leave_flow_message')}`
      const confirmed = typeof window !== 'undefined' && window.confirm(confirmMessage)
      const href = pendingSegmentLeaveHrefRef.current
      pendingSegmentLeaveHrefRef.current = null
      if (!confirmed) return
      handleTemplatesDialogClose(false)
      if (href) {
        if (href.startsWith('http://') || href.startsWith('https://')) {
          window.location.assign(href)
        } else {
          navigateWithSubmenuHandling(href)
        }
      }
    },
    [templatesDialogView, handleTemplatesDialogClose, t, navigateWithSubmenuHandling]
  )

  /** Empty-state page has no DynamicSegmentProvider — open full create with template */
  const handleTemplateSelect = (template: SegmentTemplateType) => {
    router.push(`/segments/create?templateId=${template.id}`)
  }

  /** Dialog already has provider — apply template in the slide-in dynamic flow */
  const handleTemplateSelectInDialog = (template: SegmentTemplateType) => {
    widenDialogAfterTemplatesExitRef.current = true
    setTemplatesDialogView('dynamic-creation')
    setPendingDynamicTemplateId(template.id)
  }

  const openSegment = (segment: SegmentListItemType, dynamicRoute: 'view' | 'edit') => {
    if (segment?.constructType === 'DYNAMIC') {
      void router.push(`/segments/${dynamicRoute}?segmentId=${segment.id}`)
      return
    }
    setEditSegmentId(segment?.id)
    if (segment?.constructType === 'CUSTOMER_TAG') {
      setTemplatesDialogView('customer-tag-creation')
      setIsTemplatesDialogOpen(true)
      return
    }
    setIsSegmentDialogOpen(true)
  }

  const handleEditSegment = (segment: SegmentListItemType) => {
    sendGTMTrigger('segment_edit_started', 'click', {
      segmentType: segment?.type
    })
    openSegment(segment, 'edit')
  }

  const handleSegmentRowClick = (segment: SegmentListItemType) => openSegment(segment, 'view')

  const handleSegmentSuccess = () => {
    void callGetSegments()
  }

  const deleteSegment = async () => {
    if (!selectedSegmentId) return
    setIsDeleteLoading(true)
    try {
      const response: any = await deleteSegmentById(merchant?.accountId ?? '', selectedSegmentId)
      if (response?.error) {
        errorToast(response.error)
        console.error('x-trace-id:', response.headers?.['x-trace-id'])
      } else {
        const activityLogPayload = {
          entityName: EntityName.SEGMENTS,
          actionName: t('deleted_segment'),
          metadata: {
            segment_id: selectedSegmentId,
            segment_name: segmentList?.find((segment: SegmentListItemType) => segment?.id === selectedSegmentId)?.name
          }
        }
        sendGTMTrigger('segment_deleted', 'delete')
        sendActivityLog(activityLogPayload)
        successToast('Segment deleted successfully')
        setIsDeleteConfirmOpen(false)
        setSelectedSegmentId('')
      }
    } catch (e) {
      console.error(e)
      errorToast(t('something_went_wrong'))
    } finally {
      setIsDeleteLoading(false)
      void callGetSegments()
    }
  }

  const toggleSegment = async () => {
    setIsToggleSegmentConfirmOpen(false)
    setLoaderProperties({
      isLoaderVisible: true,
      message: selectedSegmentActiveStatus ? 'Enabling segment...' : 'Disabling segment...'
    })
    try {
      const response: any = await toggleSegmentStatusById(merchant?.accountId ?? '', selectedSegmentId)
      if (response?.error) {
        errorToast(response.error)
        console.error('x-trace-id:', response.headers?.['x-trace-id'])
      } else {
        const activitiLogPayload = {
          entityName: EntityName.SEGMENTS,
          actionName: selectedSegmentActiveStatus ? t('enabled_segment') : t('disabled_segment'),
          metadata: {
            segment_id: selectedSegmentId,
            segment_name: segmentList?.find((segment: SegmentListItemType) => segment?.id === selectedSegmentId)?.name
          }
        }
        sendGTMTrigger('segment_toggled', 'toggle', {
          toggleStatus: selectedSegmentActiveStatus ? 'enabled' : 'disabled'
        })
        sendActivityLog(activitiLogPayload)
        void callGetSegments()
        successToast(`Segment ${selectedSegmentActiveStatus ? 'enabled' : 'disabled'} successfully`)
      }
    } catch (e) {
      console.error(e)
      errorToast(t('something_went_wrong'))
    } finally {
      setLoaderProperties({ isLoaderVisible: false, message: '' })
    }
  }

  return (
    <>
      <div className='flex min-h-0 flex-1 flex-col'>
        <div className='flex shrink-0 flex-col gap-0 bg-zinc-50 px-12 pt-4 pb-2'>
          <div className='flex items-center gap-2 py-2'>
            <div className='flex flex-shrink-0 items-center gap-2'>
              <div className='flex size-6 shrink-0 items-center justify-center'>
                <Icon IconComponent={UsersThree} size='md' className='text-zinc-500' iconProps={{ weight: 'duotone' }} />
              </div>
              <h3 className='text-text-lg-semibold text-zinc-800'>{t('segments')}</h3>
            </div>
            {!showTemplatesView && (
              <div className='ml-auto flex flex-row items-center gap-0.5'>
                {!isSearchExpanded ? (
                  <IconButton
                    IconComponent={MagnifyingGlass}
                    size='sm'
                    variant='ghost'
                    className='text-zinc-500'
                    onClick={() => setIsSearchExpanded(true)}
                    aria-label={t('segment_creation.tooltip_search')}
                    tooltip={{ text: t('segment_creation.tooltip_search') }}
                  />
                ) : (
                  <Input
                    type='text'
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    placeholder={t('segment_creation.search_placeholder')}
                    variant='filled'
                    dimension='sm'
                    autoFocus
                    className='w-60'
                    aria-label='Search segments'
                    startAdornment={<Icon IconComponent={MagnifyingGlass} size='sm' className='text-zinc-500' />}
                    endAdornment={
                      <IconButton
                        IconComponent={X}
                        size='xs'
                        variant='ghost'
                        onClick={() => {
                          setSearchText('')
                          setIsSearchExpanded(false)
                        }}
                      />
                    }
                  />
                )}

                <GenericSelectionModal
                  options={SegmentListSortOptions}
                  selectedKey={sortOrder}
                  onSelect={option => setSortOrder(option.key as SegmentSortOrderType)}
                  placeholderComponent={
                    <IconButton
                      IconComponent={SortAscending}
                      size='sm'
                      className='text-zinc-500'
                      variant='ghost'
                      aria-label={t('segment_creation.tooltip_sort')}
                      tooltip={{ text: t('segment_creation.tooltip_sort') }}
                    />
                  }
                  showSearch={false}
                  triggerClassName='!border-0'
                  contentClassName='w-48'
                />

                <div className='h-6 w-px bg-zinc-200 mx-2' />

                <Button id='segment-create-button' size='sm' onClick={handleCreateSegment}>
                  {t('new_segment')}
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className='flex min-h-0 min-w-0 flex-1 flex-col'>
          <div
            className={`min-h-0 min-w-0 flex-1 overflow-y-auto px-12 scrollbar-thin ${
              showTemplatesView ? 'flex flex-col' : ''
            }`}
          >
            {showTemplatesView ? (
              <TemplatesView
                openType='page'
                onCreateStaticSegment={handleCreateStaticSegment}
                onCreateDynamicSegment={handleCreateDynamicSegment}
                onCreateCustomerTagSegment={handleCreateCustomerTagSegment}
                onTemplateSelect={handleTemplateSelect}
              />
            ) : (
              <SegmentTable
                segments={segmentList}
                loading={loading}
                hasMore={hasMore}
                isLoadingMore={isLoadingMore}
                onLoadMore={loadMore}
                onRowClick={handleSegmentRowClick}
                handleEditSegment={handleEditSegment}
                handleToggleSegment={handleToggleSegment}
                handleDeleteSegment={handleDeleteSegment}
              />
            )}
          </div>
        </div>

        <DeleteConfirmationPopup
          isOpen={isDeleteConfirmOpen}
          actions={{
            onConfirm: deleteSegment,
            onClose: () => setIsDeleteConfirmOpen(false)
          }}
          config={{
            title: t('delete_segment'),
            description: t('delete_segment_description'),
            isLoading: isDeleteLoading
          }}
        />
        {isToggleSegmentConfirmOpen ? (
          <ConfirmationDialog
            jobButton={selectedSegmentActiveStatus ? t('enable') : t('disable')}
            cancelButton={t('cancel')}
            jobButtonColor='primary'
            cancelButtonColor='secondary'
            confirmationText={t('do_you_want_to_toggle_segment', {
              status: selectedSegmentActiveStatus ? 'enable' : 'disable'
            })}
            title={t('toggle_segment', { status: selectedSegmentActiveStatus ? 'Enable' : 'Disable' })}
            isOpen={isToggleSegmentConfirmOpen}
            setIsConfirmationOpen={setIsToggleSegmentConfirmOpen}
            handleJob={toggleSegment}
          />
        ) : (
          <></>
        )}
      </div>
      <ManualSegmentCreationDialog
        isOpen={isSegmentDialogOpen}
        setIsOpen={setIsSegmentDialogOpen}
        segmentId={editSegmentId}
        isEditMode={true}
        onSuccess={handleSegmentSuccess}
      />
      <GenericSlideInDialog
        isOpen={isTemplatesDialogOpen}
        setIsOpen={handleTemplatesDialogClose}
        direction='right'
        width={templatesDialogView === 'dynamic-creation' && isDynamicPreviewVisible ? `calc(100vw - ${sidebarWidth}px)` : 440}
        hideOverlay
        disableOutsideClick={templatesDialogView !== 'templates'}
        onBackdropClick={handleSegmentCreationOutsideClick}
        className={`!rounded-none !bg-zinc-50 ${isDynamicPreviewVisible ? '!shadow-none' : '!shadow-[10px_80px_30px_0_rgba(0,0,0,0.15),_-40px_-20px_48px_0_rgba(250,250,250,0.80)]'}`}
      >
        <div className='flex flex-row flex-1 min-h-0'>
          {!isDynamicPreviewVisible && <div className='w-px h-full shrink-0' style={{ background: 'linear-gradient(180deg, rgba(228, 228, 231, 0.00) 0%, #E4E4E7 25%)' }} />}
          <div className='flex flex-col flex-1 min-w-0 min-h-0'>
            <DynamicSegmentProvider
              onSuccess={() => { handleSegmentSuccess(); handleTemplatesDialogClose(false) }}
              onDiscard={() => handleTemplatesDialogClose(false)}
              templateIdToApply={pendingDynamicTemplateId}
              onTemplateApplied={clearPendingDynamicTemplate}
            >
              <AnimatePresence
                mode='popLayout'
                onExitComplete={() => {
                  if (widenDialogAfterTemplatesExitRef.current) {
                    widenDialogAfterTemplatesExitRef.current = false
                    window.setTimeout(() => setIsDynamicPreviewVisible(true), 20)
                  }
                }}
              >
                {templatesDialogView === 'templates' && (
                  <motion.div
                    key='templates'
                    className='w-full min-w-0'
                    initial={{ y: 0, opacity: 1 }}
                    exit={{ y: -10, opacity: 0 }}
                    transition={{ duration: 0.15, ease: 'easeOut' }}
                    style={{ willChange: 'transform, opacity' }}
                  >
                    <TemplatesView
                      openType='dialog'
                      onCreateStaticSegment={handleCreateStaticSegment}
                      onCreateDynamicSegment={handleCreateDynamicSegment}
                onCreateCustomerTagSegment={handleCreateCustomerTagSegment}
                      onTemplateSelect={handleTemplateSelectInDialog}
                      onClose={() => handleTemplatesDialogClose(false)}
                    />
                  </motion.div>
                )}
                {templatesDialogView === 'static-creation' && (
                  <motion.div
                    key='static-creation'
                    className='w-full min-w-0 h-full'
                    initial={{ y: 10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                    style={{ willChange: 'transform, opacity' }}
                  >
                    <SegmentProvider>
                      <ManualSegmentCreationFlow
                        isEditMode={false}
                        handleClose={() => handleTemplatesDialogClose(false)}
                        onSuccess={handleSegmentSuccess}
                      />
                    </SegmentProvider>
                  </motion.div>
                )}
                {templatesDialogView === 'customer-tag-creation' && (
                  <motion.div
                    key='customer-tag-creation'
                    className='w-full min-w-0 h-full'
                    initial={{ y: 10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                    style={{ willChange: 'transform, opacity' }}
                  >
                    <CustomerTagSegmentCreationFlow
                      segmentId={editSegmentId}
                      isEditMode={Boolean(editSegmentId)}
                      handleClose={() => handleTemplatesDialogClose(false)}
                      onSuccess={handleSegmentSuccess}
                    />
                  </motion.div>
                )}
                {templatesDialogView === 'dynamic-creation' && (
                  <motion.div
                    key='dynamic-creation'
                    className='w-full min-w-0 h-full'
                    initial={{ y: 10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                    style={{ willChange: 'transform, opacity' }}
                  >
                    <DynamicSegmentCreationFlow
                      onBack={() => setTemplatesDialogView('templates')}
                      onPreviewVisibilityChange={setIsDynamicPreviewVisible}
                      hideBreadcrumbPrefix
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </DynamicSegmentProvider>
          </div>
        </div>
      </GenericSlideInDialog>
    </>
  )
}

export default Segments
